import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useGetPaymentsQuery, useManualPaymentMutation } from "../../../Api/platformApi";
import {
  AppPage,
  PageHeader,
  DataTable,
  StatusBadge,
  ActionButton,
  type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const money = (v: any) => "$" + Number(v ?? 0).toFixed(2);

const Payments: React.FC = () => {
  const { data } = useGetPaymentsQuery();
  const [manual, { isLoading }] = useManualPaymentMutation();
  const rows = data?.data ?? [];

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({
    company_id: "",
    amount: "",
    number: "",
    cvd: "",
    expiry_month: "",
    expiry_year: "",
    save_card: false,
  });
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const charge = async () => {
    setResult(null);
    try {
      const res: any = await manual({
        company_id: Number(form.company_id),
        amount: Number(form.amount),
        save_card: form.save_card,
        card: {
          number: form.number,
          cvd: form.cvd,
          expiry_month: form.expiry_month,
          expiry_year: form.expiry_year,
        },
      }).unwrap();
      setResult({ ok: !!res.status, msg: res.message || "Done" });
    } catch (e: any) {
      setResult({ ok: false, msg: e?.data?.message || "Charge failed" });
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "paid_at",
      label: "Date",
      render: (p) =>
        p.paid_at
          ? new Date(p.paid_at).toLocaleString()
          : new Date(p.created_at).toLocaleString(),
    },
    {
      id: "invoice",
      label: "Invoice",
      render: (p) => (
        <Typography fontSize={13} fontWeight={600} color={NAVY}>
          {p.invoice?.invoice_number ?? "—"}
        </Typography>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      align: "right",
      render: (p) => (
        <Typography fontSize={13} fontWeight={700} color={NAVY}>
          {money(p.amount)}
        </Typography>
      ),
    },
    {
      id: "method",
      label: "Method",
      render: (p) => p.method ?? "—",
    },
    {
      id: "status",
      label: "Status",
      render: (p) => <StatusBadge status={p.status} />,
    },
    {
      id: "transaction_id",
      label: "Txn ID",
      render: (p) => (
        <Typography
          component="code"
          fontSize={11}
          sx={{ fontFamily: "monospace", color: "text.secondary" }}
        >
          {p.transaction_id}
        </Typography>
      ),
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<PaymentRoundedIcon />}
          title="Payments"
          subtitle="Payment history and manual charge terminal"
          actions={
            <ActionButton
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setResult(null);
                setOpen(true);
              }}
            >
              New Payment (Terminal)
            </ActionButton>
          }
        />
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        empty={{
          icon: <PaymentRoundedIcon />,
          title: "No payments yet",
          message: "Payment records will appear here once charges are processed.",
        }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Manual Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {result && (
              <Alert severity={result.ok ? "success" : "error"}>{result.msg}</Alert>
            )}
            <TextField
              label="Company ID"
              value={form.company_id}
              onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            />
            <TextField
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
            <TextField
              label="Card Number"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value })}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="MM"
                value={form.expiry_month}
                onChange={(e) => setForm({ ...form, expiry_month: e.target.value })}
                sx={{ width: 70 }}
              />
              <TextField
                label="YYYY"
                value={form.expiry_year}
                onChange={(e) => setForm({ ...form, expiry_year: e.target.value })}
                sx={{ width: 90 }}
              />
              <TextField
                label="CVV"
                value={form.cvd}
                onChange={(e) => setForm({ ...form, cvd: e.target.value })}
                sx={{ width: 70 }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={form.save_card}
                  onChange={(e) => setForm({ ...form, save_card: e.target.checked })}
                />
              }
              label="Save card on file"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={charge}
            disabled={isLoading || !form.company_id || !form.amount || !form.number}
          >
            Charge
          </Button>
        </DialogActions>
      </Dialog>
    </AppPage>
  );
};

export default Payments;
